import { useEffect, useState } from "react";
import { Box, Button, Dialog, Flex, Switch, Text, TextField } from "@radix-ui/themes";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: { name: string; description?: string; isShared: boolean }) => Promise<void>;
}

/**
 * Lightweight "New dashboard" dialog. Collects a name, optional description and
 * a share toggle. The caller handles the POST + navigation — this stays purely
 * presentational so it can be reused from anywhere (top bar, empty-state, etc.).
 */
export function NewDashboardModal({ open, onOpenChange, onSubmit }: Props) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isShared, setIsShared] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setName("");
      setDescription("");
      setIsShared(false);
      setSaving(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
        isShared,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="480px">
        <Dialog.Title>New dashboard</Dialog.Title>
        <Dialog.Description size="2" mb="4" color="gray">
          Create a personal dashboard. You can share it with teammates later if you want.
        </Dialog.Description>

        <Flex direction="column" gap="3">
          <Box>
            <Text size="2" weight="medium" mb="1" asChild>
              <label>Name</label>
            </Text>
            <TextField.Root
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Growth KPIs"
              autoFocus
            />
          </Box>
          <Box>
            <Text size="2" weight="medium" mb="1" asChild>
              <label>Description (optional)</label>
            </Text>
            <TextField.Root
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description shown under the title"
            />
          </Box>
          <Flex align="center" gap="2" mt="1">
            <Switch checked={isShared} onCheckedChange={setIsShared} />
            <Text size="2">Share with everyone in this org</Text>
          </Flex>
        </Flex>

        <Flex gap="2" mt="5" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray" type="button">Cancel</Button>
          </Dialog.Close>
          <Button onClick={handleSubmit} disabled={!name.trim() || saving}>
            {saving ? "Creating…" : "Create dashboard"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
